import { namespace } from '@common-config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './calendar-grid.module.scss';
import { buildChipViewModel } from './layout/chip-view-model';
import type { DragState } from './use-calendar-drag';
import type { CalendarGridView } from './types';

interface Props {
    drag: DragState;
    view: CalendarGridView;
    now: number;
    /**
     * Why a release here would be refused, already said in the user's own words.
     * `null` when the drop is fine.
     */
    refusal?: string | null;
}

/**
 * The thing being dragged, drawn at the cursor.
 *
 * Dimming the original chip alone reads as "something is off", not as "you are
 * holding this". A card that follows the pointer — and states the time it would
 * land on — makes the gesture legible, and tells the user what a release does
 * before they commit to it.
 *
 * That promise has to hold BOTH ways. When the sequence will not accept the cell
 * under the pointer, the card says so here, in the same place it would otherwise
 * have promised a landing time — so the answer arrives while the user can still
 * act on it, instead of as a dialog after they have let go.
 */
export const DragPreview: React.FC<Props> = ({ drag, view, now, refusal }) => {
    const { t, i18n } = useTranslation(namespace);
    const vm = buildChipViewModel(drag.event, now);

    const landing = drag.targetMs == null
        ? null
        : view === 'month'
            // Month keeps the source time of day, so the preview says the date.
            ? formatInLanguage(drag.targetMs, i18n.language, t('calendar.grid.dragDateFormat'))
            : formatInLanguage(drag.targetMs, i18n.language, t('calendar.grid.dragTimeFormat'));

    return (
        <div
            className={[styles.dragPreview, refusal ? styles.dragPreviewRefused : ''].filter(Boolean).join(' ')}
            style={{ left: drag.pointerX + 14, top: drag.pointerY + 12, ['--chip-rail' as any]: vm.railColor }}
            aria-hidden
        >
            <div className={styles.dragPreviewTitle}>{vm.title}</div>
            {refusal ? (
                <div className={styles.dragPreviewRefusal}>{refusal}</div>
            ) : (
                <div className={styles.dragPreviewTime}>
                    {landing ?? t('calendar.grid.releaseToMove')}
                </div>
            )}
        </div>
    );
};
